import React, { useState, useEffect } from 'react';
import { AlertTriangle, Trash2, Loader } from 'lucide-react';
import ApiService from './ApiService';
import RoleModal from './RoleModal';

function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [errorModal, setErrorModal] = useState(null);

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getRoles();
      setRoles(data);
      setError(null);
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const data = await ApiService.getPermissions();
      setPermissions(data);
    } catch (err) {
      console.error('Error loading permissions:', err);
    }
  };

  const handleCreateRole = () => {
    setSelectedRole(null);
    setShowModal(true);
  };

  const handleEditRole = async (roleId) => {
    try {
      const roleData = await ApiService.getRole(roleId);
      setSelectedRole(roleData);
      setShowModal(true);
    } catch (err) {
      console.error('Error loading role details:', err);
      setErrorModal({ title: 'Error Loading Role', message: err.message || 'Failed to load role details. Please try again.' });
    }
  };

  const handleDeleteRole = (roleId, roleName) => {
    setRoleToDelete({ roleId, roleName });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      await ApiService.deleteRole(roleToDelete.roleId);
      setRoles(roles.filter(r => r.roleId !== roleToDelete.roleId));
      setShowDeleteConfirm(false);
      setRoleToDelete(null);
    } catch (err) {
      console.error('Error deleting role:', err);
      
      // Parse the error response
      let errorTitle = 'Error Deleting Role';
      let errorMessage = 'Failed to delete role';
      
      try {
        // Check if error message contains JSON
        const jsonMatch = err.message.match(/\{.*\}/);
        if (jsonMatch) {
          const errorData = JSON.parse(jsonMatch[0]);
          
          if (errorData.isSystem === true) {
            // System role deletion attempt
            errorTitle = 'System Role Protected';
            errorMessage = `Cannot delete "${errorData.roleName || roleToDelete.roleName}" because it is a system role.\n\nSystem roles are pre-configured roles that are essential for the application and cannot be deleted.`;
          } else if (errorData.message && errorData.message.includes('Cannot delete role')) {
            errorTitle = 'Role In Use';
            const userCount = errorData.userCount || 0;
            const users = errorData.users || [];
            
            errorMessage = `Cannot delete "${roleToDelete.roleName}" because it is currently assigned to ${userCount} user${userCount !== 1 ? 's' : ''}.`;
            
            if (users.length > 0) {
              errorMessage += `\n\nUsers with this role:\n• ${users.join('\n• ')}`;
            }
            
            errorMessage += '\n\nPlease remove this role from all users before deleting it.';
          } else {
            errorMessage = errorData.message || 'Failed to delete role';
          }
        } else {
          errorMessage = err.message || 'Failed to delete role';
        }
      } catch (parseErr) {
        console.error('Error parsing error message:', parseErr);
        errorMessage = err.message || 'Failed to delete role';
      }
      
      setErrorModal({ title: errorTitle, message: errorMessage });
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedRole(null);
  };

  const handleModalSave = () => {
    setShowModal(false);
    setSelectedRole(null);
    loadRoles();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading roles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Roles & Permissions</h1>
        <button
          onClick={handleCreateRole}
          className="bg-spruce-600 hover:bg-spruce-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Role
        </button>
      </div>

      {roles.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No roles yet</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first role</p>
          <button
            onClick={handleCreateRole}
            className="bg-spruce-600 hover:bg-spruce-700 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create First Role
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roles.map((role) => (
            <div 
              key={role.roleId} 
              className={`rounded-lg shadow hover:shadow-lg transition-shadow ${
                role.isSystem 
                  ? 'bg-spruce-50 border border-spruce-200' 
                  : 'bg-white'
              }`}
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-xl font-semibold text-gray-800">{role.roleName}</h3>
                      {role.isSystem && (
                        <span className="px-2 py-1 text-xs font-semibold bg-spruce-600 text-white rounded-full flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                          System
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{role.description || 'No description'}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditRole(role.roleId)}
                      className="text-spruce-600 hover:text-spruce-700"
                      title="Edit role"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    {!role.isSystem && (
                      <button
                        onClick={() => handleDeleteRole(role.roleId, role.roleName)}
                        className="text-red-600 hover:text-red-700"
                        title="Delete role"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <div className="border-t pt-4">
                  <div className="text-xs text-gray-500">
                    Created {new Date(role.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <RoleModal
          role={selectedRole}
          permissions={permissions}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && roleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Delete Role?</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete the role <strong>"{roleToDelete.roleName}"</strong>?
            </p>
            <p className="text-sm text-gray-600 mb-6">
              Users assigned to this role will lose their permissions.
            </p>
            <p className="text-red-600 font-semibold mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Yes, Delete
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setRoleToDelete(null);
                }}
                disabled={deleting}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">{errorModal.title}</h3>
            </div>
            <p className="text-gray-600 mb-6 whitespace-pre-line">
              {errorModal.message}
            </p>
            <button
              onClick={() => setErrorModal(null)}
              className="w-full bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default RolesPage;